import { Form, Input, InputNumber, message, Switch, Tooltip } from 'antd';
import { Rule } from 'antd/es/form';
import TextArea from 'antd/es/input/TextArea';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { AiOutlineEnter } from 'react-icons/ai';
import AccountList from 'src/app/components/AccountList/AccountList';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import OrderedTags from 'src/app/components/OrderedTagsComponent/OrderedTags';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { interestImageValidator } from 'src/app/components/UploadInterestImage/upload-interest-image.config';
import UploadInterestImage from 'src/app/components/UploadInterestImage/UploadInterestImage';
import { IFormProps } from 'src/app/forms/form.config';
import { convertPersonRecordToEditFormData } from 'src/app/forms/PersonIdentifiedForm/person-identified-form.config';
import { getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { convertToSelectOptionsWithId } from 'src/app/helpers/form.helper';
import {
  useCreatePersonMutation,
  useGetPersonsIdentifiedAllQuery,
  useUpdatePersonMutation,
} from 'src/app/redux/interests/persons/personIdentifiedSlice';
import { useGetPersonsRecognizedQuery } from 'src/app/redux/interests/persons/personRecognizedSlice';
import { useLearnInterestMutation } from 'src/app/redux/learner/learnerSlice';
import { useGetSeedsQuery } from 'src/app/redux/seeds/seedsSlice';
import { InterestsTypes } from 'src/enums/interests.enum';
import { CollarType, PersonStatus } from 'src/enums/nginr-type.enum';
import { Account } from 'src/types/account.type';

const validator = (_rule: any, value: number) => {
  if (value && value < 0) {
    return Promise.reject(new Error(t('errors.negativeNumbersAreNotAccepted')));
  }
  return Promise.resolve();
};
const heightAndWeightRule: Rule[] = [{ required: true }, { validator }];

interface IPersonIdentifiedForm extends IFormProps {
  // blockId?: string;
  learn?: boolean;
}

const PersonIdentifiedForm: React.FC<IPersonIdentifiedForm> = ({ form, recordId, destroyModal, learn }) => {
  const [deletedPictures, setDeletedPictures] = useState<string[]>([]);
  const [aliases, setAliases] = useState<string[]>([]);
  const [learnStatus, setLearnStatus] = useState(!!learn);
  const [accounts, setAccounts] = useState<Account[]>([]);

  const { data: seeds } = useGetSeedsQuery();

  const { data: personRecognizedList } = useGetPersonsRecognizedQuery();

  const { data: editData } = useGetPersonsIdentifiedAllQuery(
    { ids: [recordId!] },
    {
      skip: !recordId,
      selectFromResult: ({ data }) => ({
        data: data?.rows.find((row) => row.id === recordId),
      }),
      refetchOnMountOrArgChange: true,
    },
  );

  const [createPerson] = useCreatePersonMutation();
  const [updatePerson] = useUpdatePersonMutation();
  const [learnPerson] = useLearnInterestMutation();

  const onFinish = (values: any) => {
    if (!recordId) {
      createPerson({
        ...values,
        collarId: values.whiteCollarId ?? values.blueCollarId,
        accounts,
      })
        .unwrap()
        .then(() => {
          message.success(t('success.added'));
          if (destroyModal) destroyModal();
          // if (learn)
          //   learnPerson({})
          //     .unwrap()
          //     .then(() => message.success('Learn process has began successfully!'));
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    } else {
      updatePerson({
        id: recordId,
        collarId: values.whiteCollarId ?? values.blueCollarId,
        deletedPictures: deletedPictures.length ? deletedPictures : undefined,
        accounts,
        ...values,
      })
        .unwrap()
        .then(() => {
          message.success(t('success.edited'));
          if (destroyModal) destroyModal();
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    }
  };

  useEffect(() => {
    if (editData) {
      form.setFieldsValue(convertPersonRecordToEditFormData(editData));
      setAliases(editData.aliases.map((alias) => alias.value));
      setAccounts(editData.accounts ?? []);
    }
  }, [editData]);

  useEffect(() => {
    form.setFieldValue('aliases', aliases);
  }, [aliases]);

  useEffect(() => {
    form.setFieldValue('learn', learnStatus);
  }, [learnStatus]);

  return (
    <FormComponent options={{ form, onFinish }}>
      <Form.Item label={t('forms.personIdentifiedForm.gender')} name="genderId" rules={[{ required: true }]}>
        <SearchableSelect
          variant="borderless"
          options={convertToSelectOptionsWithId(personRecognizedList?.rows || [])}
        />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.age')} name="ageId" rules={[{ required: true }]}>
        <SearchableSelect variant="borderless" options={convertToSelectOptionsWithId(seeds?.ages || [])} />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.name')} name="name" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.surname')} name="surname" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.aliases')} name="aliases">
        <div style={{ display: 'flex', width: '100%' }}>
          <Input variant="borderless" onPressEnter={(e: any) => setAliases((prev) => [...prev, e.target.value])} />
          <div
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => {
              const alias = form.getFieldValue('aliases');
              if (!Array.isArray(alias)) setAliases((prev) => [...prev, alias]);
            }}
          >
            <span style={{ color: 'white' }}>{t('global.enter')}</span>
            <AiOutlineEnter size={26} color="white" />
          </div>
        </div>
      </Form.Item>

      <OrderedTags tags={aliases} setTags={setAliases} />
      <Form.Item label={t('forms.personIdentifiedForm.email')} name="email" rules={[{ type: 'email' }]}>
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.status')} name="status">
        <SearchableSelect variant="borderless" options={getEnumSelectOptions(PersonStatus)} />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.nationality')} name="nationalityId">
        <SearchableSelect variant="borderless" options={convertToSelectOptionsWithId(seeds?.nationalities || [])} />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.organization')} name="organization">
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.height')} name="height" rules={[heightAndWeightRule[1]]}>
        <InputNumber
          formatter={(value) => (value ? value.toString().replace(/\.\d*$/, '') : '')}
          parser={(value) => (value ? value.replace(/\.\d*$/, '') : '')}
          step={1}
          variant="borderless"
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.weight')} name="weight" rules={[heightAndWeightRule[1]]}>
        <InputNumber variant="borderless" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item shouldUpdate={(prev, curr) => prev.blueCollarId !== curr.blueCollarId} noStyle>
        {({ getFieldValue }) => (
          <Form.Item label={t('forms.personIdentifiedForm.whiteCollar')} name="whiteCollarId">
            <SearchableSelect
              allowClear
              disabled={getFieldValue('blueCollarId') ?? false}
              variant="borderless"
              options={convertToSelectOptionsWithId(
                seeds?.collars.filter((collar) => collar.type === CollarType.White_Collar) || [],
              )}
            />
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item shouldUpdate={(prev, curr) => prev.whiteCollarId !== curr.whiteCollarId} noStyle>
        {({ getFieldValue }) => (
          <Form.Item label={t('forms.personIdentifiedForm.blueCollar')} name="blueCollarId">
            <SearchableSelect
              allowClear
              disabled={getFieldValue('whiteCollarId') ?? false}
              variant="borderless"
              options={convertToSelectOptionsWithId(
                seeds?.collars.filter((collar) => collar.type === CollarType.Blue_Collar) || [],
              )}
            />
          </Form.Item>
        )}
      </Form.Item>

      <Form.Item label={t('forms.personIdentifiedForm.description')} name="description">
        <TextArea variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.personIdentifiedForm.learnStatus')} name="learn">
        <div style={{ display: 'flex', width: '100%' }}>
          <Tooltip title={learn ? t('global.learnIsActive') : ''} placement="left">
            <Switch
              onChange={(e) => setLearnStatus(e)}
              style={{ marginLeft: 'auto' }}
              defaultChecked={!!learn}
              checkedChildren={t('global.detected')}
              unCheckedChildren={t('global.notDetected')}
              disabled={!!learn}
            />
          </Tooltip>
        </div>
      </Form.Item>
      <Form.Item label="Accounts">
        <AccountList accounts={accounts} setAccounts={setAccounts} />
      </Form.Item>
      <Form.Item
        label={t('forms.personIdentifiedForm.pictures')}
        name="pictures"
        rules={[{ validator: interestImageValidator }]}
      >
        <UploadInterestImage
          form={form}
          interestType={InterestsTypes.Person}
          recordId={recordId}
          setDeletedPictures={setDeletedPictures}
          defaultFileList={form.getFieldValue('pictures') ?? []}
        />
      </Form.Item>
    </FormComponent>
  );
};

export default PersonIdentifiedForm;
